import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * 공유 UI 컴포넌트.
 *
 * 도메인 화면에서 버튼·카드 스타일을 직접 정의하지 않는다. (CLAUDE.md 5.2절)
 * 각자 만들면 같은 버튼이 화면마다 조금씩 다른 색과 여백을 갖게 된다.
 *
 * 컴포넌트 수가 늘면 파일을 분리한다. 지금은 한 파일로 충분하다.
 */

/** 클래스 이름을 합친다. falsy 값은 걸러낸다. */
export function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * 입력 요소 공통 스타일.
 *
 * input·select 를 컴포넌트로 감싸지 않고 클래스만 공유하는 이유
 *   래핑하면 type·autoComplete·inputMode 같은 속성을 전부 다시 뚫어줘야 하고,
 *   결국 HTML 속성을 그대로 통과시키는 얇은 껍데기가 된다.
 *   지금 필요한 것은 생김새를 맞추는 것뿐이다.
 *
 * 최소 높이 44px 는 모바일 터치 대상 권장값이다 (애플 HIG).
 */
export const FIELD_CLASS =
  'min-h-[44px] w-full rounded-lg border border-line-strong bg-surface px-3 text-sm ' +
  'disabled:cursor-not-allowed disabled:bg-surface-hover';

// ─────────────────────────────────────────────
// Button
// ─────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  fullWidth?: boolean;
}

/*
  primary 를 브랜드 파랑으로 바꿨다 (2026-08-16).

  그전에는 surface-inverse 였다 — 라이트에서 검정, 다크에서 흰색이 되는 색이다.
  기능적으로는 문제가 없었지만 "이 화면의 주 동작" 이라는 신호가 없었다.
  검정 버튼은 어디에나 있는 색이라 눈이 그것을 특별하게 보지 않는다.

  hover 에 opacity 를 쓰지 않고 별도 색을 쓰는 이유
    opacity 를 낮추면 배경이 비쳐 색이 탁해진다. 라이트에서는 흰색이 섞여 바래고
    다크에서는 남색이 섞여 가라앉는다. 두 모드에서 반대 방향으로 어긋난다.
    --brand-hover 는 라이트에서 어둡게, 다크에서 밝게 각각 정의되어 있다.
*/
const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-brand-fg hover:bg-brand-hover',
  secondary: 'bg-surface text-fg border border-line-strong hover:bg-surface-hover',
  ghost: 'bg-transparent text-fg-muted hover:bg-surface-hover',
  danger: 'bg-danger text-danger-fg hover:opacity-90',
};

export function Button({
  variant = 'primary',
  fullWidth = false,
  className,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      // type 기본값을 button으로 둔다.
      // HTML 기본값은 submit이라 폼 안에서 아무 버튼이나 눌러도 폼이 제출된다.
      type={type}
      className={cx(
        // 모바일 터치 대상은 최소 44px 높이가 필요하다 (애플 HIG 권장).
        'inline-flex min-h-[44px] items-center justify-center rounded-lg px-4 text-sm font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANT[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    />
  );
}

// ─────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────

interface CardProps {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Card({ title, action, children, className }: CardProps) {
  return (
    <section
      className={cx('rounded-xl border border-line bg-surface p-4 shadow-sm', className)}
    >
      {(title || action) && (
        <header className="mb-3 flex items-center justify-between gap-2">
          {title && <h2 className="text-sm font-semibold text-fg-muted">{title}</h2>}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

// ─────────────────────────────────────────────
// Badge
// ─────────────────────────────────────────────

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const BADGE_TONE: Record<BadgeTone, string> = {
  // 배경과 글자를 쌍으로 정의한 토큰을 쓴다.
  // 밝은 배경 + 진한 글자 조합은 다크 모드에서 그대로 뒤집을 수 없어
  // (밝은 배경만 남으면 눈이 부시다) index.css 에서 쌍으로 갈아끼운다.
  neutral: 'bg-badge-neutral text-badge-neutral-fg',
  success: 'bg-badge-success text-badge-success-fg',
  warning: 'bg-badge-warning text-badge-warning-fg',
  danger: 'bg-badge-danger text-badge-danger-fg',
  info: 'bg-badge-info text-badge-info-fg',
};

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: BadgeTone;
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        BADGE_TONE[tone],
      )}
    >
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────
// Table
// ─────────────────────────────────────────────

export function Table({ children }: { children: ReactNode }) {
  return (
    // 모바일에서 열이 많으면 가로 스크롤이 필요하다.
    // 이걸 안 하면 표가 화면 밖으로 삐져나가 페이지 전체가 가로로 흔들린다.
    <div className="-mx-4 overflow-x-auto px-4">
      <table className="w-full min-w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

/**
 * 표 머리 한 칸.
 *
 * 🔴 제목은 가운데 정렬이다. 값의 정렬을 따라가지 않는다 (2026-09-03 사용자 확정)
 *
 *   2026-08-31 에 반대로 했었다 - "제목은 가운데인데 값은 오른쪽이라 어느 제목이
 *   어느 열인지 눈으로 따라가기 어렵다" 는 판단으로 27곳을 값에 맞춰 오른쪽으로
 *   보냈다. 사용자가 실제 화면을 보고 뒤집었다: **제목은 모바일·데스크탑 불문
 *   가운데가 낫다.**
 *
 *   값 쪽은 그대로다 - 금액은 오른쪽 정렬이 기본이고 자릿수가 세로로 맞는다.
 *   그래서 지금은 제목(가운데)과 값(오른쪽)이 어긋나지만, 열 폭이 좁아
 *   실제로는 잘 읽힌다는 것이 사용자의 판단이다.
 *
 *   align 은 이제 값 열의 성격을 나타내는 표시로만 남는다 - 제목의 가로 정렬을
 *   바꾸지 않는다. 지우지 않는 이유는 호출부 수십 곳이 이미 넘기고 있고,
 *   나중에 "이 열은 숫자 열" 이라는 정보가 필요해질 수 있어서다.
 *
 * 🔴 세로 가운데 + 줄바꿈 금지 (2026-08-31 사용자 피드백)
 *   열이 좁으면 "매수 기준가" 가 두 줄로 갈라지면서 옆 열 머리와
 *   높이가 어긋나 제목이 서로 붙어 보였다. 제목은 짧으므로
 *   넘치더라도 한 줄로 두는 편이 읽힌다 - 표는 가로로 스크롤된다.
 */
export function Th({
  children,
  align = 'left',
}: {
  children: ReactNode;
  /**
   * 이 열의 값이 어느 쪽으로 붙는가. 제목의 정렬은 바꾸지 않는다.
   * 정렬 화살표 같은 부속을 붙일 때 방향을 알아야 해서 남겨 둔다.
   */
  align?: 'left' | 'right' | 'center';
}) {
  // align 을 읽지만 정렬에는 쓰지 않는다. 위 주석 참조
  void align;

  return (
    <th
      className={cx(
        'border-b border-line py-2 align-middle text-xs font-medium text-fg-subtle',
        'whitespace-nowrap text-center',
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  numeric = false,
  className,
}: {
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  /** 숫자 열이면 true. 자릿수가 세로로 맞아 비교하기 쉬워진다. */
  numeric?: boolean;
  className?: string;
}) {
  return (
    <td
      className={cx(
        'border-b border-line py-2',
        /*
          🔴 오른쪽 정렬 열은 숫자 열이라는 관례다 (SortableTh 주석과 같은 전제)

            두 가지를 여기서 준다 - numeric 을 깜빡한 셀까지 덮는 그물이다.

            overflow-wrap:normal  "13,610원" 이 두 줄로 쪼개지는 것을 막는다.
                                  nowrap 은 걸지 않는다 - 오른쪽 정렬 텍스트 열
                                  (예: '이유')이 넘친다.
            tabular-nums          자릿수 폭을 고정해 표에서 숫자 세로줄이 맞는다.
                                  숫자가 아닌 글자에는 영향이 없어 안전하다.

            43곳이 numeric 을 빠뜨리고 있었다(2026-08-31 전수 조사). 개별로
            고치면 또 빠진다 - 관례를 컴포넌트가 지키게 한다.

          🔴 가로 정렬은 가운데다. 이름과 달리 오른쪽이 아니다 (2026-09-03)

            제목(Th)이 가운데인데 값만 오른쪽이라 둘이 어긋나 있었다.
            사용자가 실제 화면을 보고 "th와 그 아래 내용의 정렬을 맞추자" 고
            정했다. 그래서 이 prop 은 이제 "이 열은 숫자 열" 이라는 뜻만 갖고,
            어느 쪽에 붙일지는 컴포넌트가 정한다.

            tabular-nums 는 그대로 둔다 - 가운데 정렬이어도 자릿수 폭이 같으면
            줄마다 숫자가 흔들리지 않는다.

            예외는 '종목' 열이다. 그쪽은 align 을 주지 않아(기본 left) 왼쪽에
            그대로 붙는다 - 이름은 길이가 제각각이라 가운데로 두면 들쭉날쭉하다.
         */
        align === 'right' && 'text-center tabular [overflow-wrap:normal]',
        align === 'center' && 'text-center',
        numeric && 'tabular',
        className,
      )}
    >
      {children}
    </td>
  );
}

// ─────────────────────────────────────────────
// EmptyState
// ─────────────────────────────────────────────

/**
 * 데이터가 없을 때 표시한다.
 *
 * 선별 조건을 통과한 종목이 0개면 "추천 후보 없음"을 그대로 보여준다.
 * 기준을 완화해 억지 후보를 만들지 않는다. (CLAUDE.md 1.3절)
 * 빈 화면은 실패가 아니라 정직한 결과다.
 */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <p className="text-sm font-medium text-fg">{title}</p>
      {description && <p className="text-xs text-fg-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────
// 상태 표시
// ─────────────────────────────────────────────

export function Spinner({ label = '불러오는 중' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-fg-muted">
      <span
        className="h-4 w-4 animate-spin rounded-full border-2 border-line-strong border-t-fg-muted"
        aria-hidden="true"
      />
      <span>{label}</span>
    </div>
  );
}

/**
 * 오류 표시.
 *
 * 백엔드 GlobalExceptionHandler가 만든 한국어 메시지를 그대로 보여준다.
 * 프론트에서 메시지를 다시 쓰면 백엔드와 표현이 갈린다.
 */
export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
      <p className="text-sm text-danger">{message}</p>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          다시 시도
        </Button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Pager
// ─────────────────────────────────────────────

/**
 * 이전/다음 페이지 이동 바. 목록 카드의 하단에 붙인다.
 *
 * 이력성 목록이 세로로 한없이 길어지는 것을 막는 장치다. (2026-08-20 사용자 요청)
 * 한 페이지 분량은 화면이 정한다 - 표는 10건, 회고처럼 긴 항목은 5건 식이다.
 * 지난 추천(서버 페이징)과 모의투자 청산·회고(클라이언트 슬라이스)가 같이 쓴다 -
 * 페이징이 어디서 일어나든 이동 UI 는 같아야 한다.
 *
 * 페이지가 하나뿐이면 아무것도 그리지 않는다. 이동할 곳이 없는데
 * 비활성 버튼 두 개가 자리를 차지할 이유가 없다.
 */
export function Pager({
  page,
  totalPages,
  onChange,
}: {
  /** 0부터 시작하는 현재 페이지 */
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
      <Button variant="secondary" disabled={page === 0} onClick={() => onChange(page - 1)}>
        이전
      </Button>

      <span className="text-xs tabular text-fg-muted">
        {page + 1} / {totalPages}
      </span>

      <Button
        variant="secondary"
        disabled={page >= totalPages - 1}
        onClick={() => onChange(page + 1)}
      >
        다음
      </Button>
    </div>
  );
}
